import { Suspense } from "react";
import { MdOpenInNew } from "react-icons/md";
import { useIntl, FormattedMessage, defineMessage } from "react-intl";
import { Link, useLocation, useParams } from "react-router";
import ErrorPage from "@/pages/_error";
import Button from "@/components/Button";
import EntityProfilePic from "@/components/EntityProfilePic";
import Note from "@/components/Note";
import SignupCallout from "@/components/SignupCallout";
import { SocialSharingDropdown } from "@/components/SocialSharingDropdown";
import SubjectSubscriptionButton from "@/components/SubjectSubscriptionButton";
import { RelativeTime } from "@/components/RelativeTime";
import TopicComments from "@/components/TopicComments";
import TopicCommentsForm from "@/components/TopicCommentsForm";
import TopicHelmet from "@/components/TopicHelmet";
import TopicLink from "@/components/TopicLink";
import UserMarkdown from "@/components/UserMarkdown";
import VotableOrderSelect from "@/components/VotableOrderSelect";
import VoteDisplay from "@/components/VoteDisplay";
import { getTopicPath } from "@/utils/routing";
import { useVotableOrder } from "@/utils/hooks";
import { SiArxiv } from "react-icons/si";
import { FaGithub, FaGoogle } from "react-icons/fa6";
import TopicDeleteButton from "@/components/TopicDeleteButton";
import {
  DEFAULT_VOTABLE_ORDER,
  TopicVotableOrderContext,
  getVotableOrderParam,
} from "@/utils/votableOrder";
import { graphql, Environment, useFragment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { TopicPageQuery as TopicPageQueryType } from "./__generated__/TopicPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { TopicPageFragment$key } from "./__generated__/TopicPageFragment.graphql";
import { Redirect } from "@/components/Redirect";
interface WebsiteIconMapEntry {
  icon: JSX.Element;
  name?: string;
}
export default function TopicPage() {
  const intl = useIntl();
  const { slug: parent } = useParams();
  const {
    topic,
    query: { viewer },
    variables: { order: initialOrder },
  } = useTopicPageQuery();

  const [order, setOrder] = useVotableOrder(initialOrder);
  const location = useLocation();

  if (!topic) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({ defaultMessage: "Data set not found" })}
      />
    );
  }

  const expectedLocationPath = getTopicPath({
    ...topic,
    forum: {
      ...topic.forum,
      owner: {
        ...topic.forum.owner,
        parent,
      },
    },
  });

  if (location.pathname !== expectedLocationPath) {
    return <Redirect to={expectedLocationPath} />;
  }

  const hostname = (topic.url && new URL(topic?.url).hostname) || "default";
  const websiteData = websiteIconMap[hostname] || websiteIconMap.default;
  return (
    <TopicVotableOrderContext.Provider value={order}>
      <TopicHelmet topic={topic} parent={parent} />
      <div className="flex flex-col pt-2 pb-8">
        <div className="flex flex-row gap-4" role="topic">
          <div className="flex flex-col gap-2 items-center">
            <EntityProfilePic entity={topic.author} size="10" />
            <Link
              to={`/${topic.author.username}`}
              className="underline text-gray-400"
            >
              {topic.author.username}
            </Link>
            <VoteDisplay subject={topic} kind="vertical" />
          </div>

          <div className="flex flex-col gap-4 flex-1">
            <h3 className="text-sm">
              <FormattedMessage
                defaultMessage="Posted {createdAt}"
                values={{
                  createdAt: <RelativeTime date={new Date(topic.createdAt)} />,
                }}
              />
            </h3>

            <div className="flex flex-row gap-4 items-center justify-between flex-wrap">
              {topic.url ? (
                <Link
                  to={topic.url}
                  target="_blank"
                  className="flex flex-wrap items-center gap-1"
                >
                  <h1 className="text-2xl font-bold font-poppins">
                    {topic.title}
                  </h1>
                  {topic.url && (
                    <Note icon={<MdOpenInNew />} className="text-xs">
                      <FormattedMessage defaultMessage="External link" />
                    </Note>
                  )}
                </Link>
              ) : (
                <h1 className="text-2xl font-bold font-poppins">
                  {topic.title}
                </h1>
              )}

              <div className="flex flex-row items-center gap-6">
                <SubjectSubscriptionButton subject={topic} />

                <SocialSharingDropdown
                  title={intl.formatMessage(
                    { defaultMessage: "Sharing {topicTitle}" },
                    { topicTitle: topic.title },
                  )}
                  injectedText={intl.formatMessage(
                    {
                      defaultMessage:
                        "Let's take look to: `{topicTitle}` on Aqora",
                    },
                    { topicTitle: topic.title },
                  )}
                />

                {topic.viewerCanEdit && (
                  <TopicLink topic={topic} subpath="edit" parent={parent}>
                    <Button kind="text">
                      <FormattedMessage defaultMessage="Edit" />
                    </Button>
                  </TopicLink>
                )}

                {topic.viewerCanDelete && <TopicDeleteButton topic={topic} />}
              </div>
            </div>

            <div className="pt-5 pb-10">
              {topic.description ? (
                <UserMarkdown>{topic.description}</UserMarkdown>
              ) : (
                <p className="text-gray-400">
                  <FormattedMessage defaultMessage="No description" />
                </p>
              )}
              {topic.url && (
                <Link to={topic.url} target="_blank" className="mx-12">
                  <Button
                    kind="secondary"
                    className="flex flex-row items-center gap-2"
                  >
                    {websiteData.icon}
                    <FormattedMessage
                      defaultMessage="View {name} page"
                      values={{ name: websiteData.name }}
                    />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        <hr />

        <div className="py-5">
          <TopicCommentsForm topic={topic} viewer={viewer} order={order} />
          <div>
            <FormattedMessage defaultMessage="Order by:" />{" "}
            <VotableOrderSelect value={order} onChange={setOrder} />
          </div>
          <Suspense fallback={<TopicComments.Skeleton />}>
            <TopicComments topic={topic} order={order} />
          </Suspense>
        </div>
        <SignupCallout {...cta} className="sm:w-2/3 self-center" />
      </div>
    </TopicVotableOrderContext.Provider>
  );
}
const websiteIconMap: Record<string, WebsiteIconMapEntry> = {
  "arxiv.org": {
    icon: <SiArxiv />,
    name: "arXiv",
  },
  "github.com": {
    icon: <FaGithub />,
    name: "GitHub",
  },
  "google.com": {
    icon: <FaGoogle />,
    name: "Google",
  },
  default: { icon: <MdOpenInNew /> },
};
const cta = Object.freeze({
  title: defineMessage({ defaultMessage: `Want to join this discussion?` }),
  body: defineMessage({
    defaultMessage: `Join our community today and start discussing with our members by participating in exciting events, competitions, and challenges. Sign up now to engage with quantum experts!`,
  }),
});
const Fragment = graphql`
  fragment TopicPageFragment on Topic {
    id
    title
    url
    createdAt
    forum {
      slug
      owner {
        slug
        forumOwnerKind
      }
    }
    author {
      username
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    description
    viewerCanEdit: viewerCan(action: UPDATE_TOPIC)
    viewerCanDelete: viewerCan(action: DELETE_TOPIC)
    ...TopicCommentsFormTopicFragment
    ...TopicCommentsFragment @arguments(order: $order)
    ...VoteDisplayFragment
    ...SubjectSubscriptionButtonFragment
    ...TopicHelmetFragment
    ...TopicLinkFragment
    ...TopicDeleteButtonFragment
  }
`;
const TopicPageQuery = graphql`
  query TopicPageQuery($id: ID!, $order: VotableOrder!) {
    viewer @ifAllowed {
      ...TopicCommentsFormViewerFragment
    }
    node(id: $id) @catch {
      __typename @required(action: THROW)
      ... on Topic {
        ...TopicPageFragment
      }
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { topicId: id }, request }: LoaderArgs) => {
    const order = getVotableOrderParam(request) || DEFAULT_VOTABLE_ORDER;
    if (!id) {
      throw new Response("No id", { status: 500 });
    }
    return preload<TopicPageQueryType>(environment, TopicPageQuery, {
      id,
      order,
    });
  };
};
const useTopicPageQuery = () => {
  const { query, ...preloaded } = usePreloaded<TopicPageQueryType>();

  if (!query.node.ok) {
    throw new Response(`Topic ${preloaded.variables.id} not found`, {
      status: 404,
    });
  }

  const topic = useFragment<TopicPageFragment$key>(
    Fragment,
    query.node.value.__typename === "Topic" ? query.node.value : null,
  );

  return {
    ...preloaded,
    topic,
    query,
  };
};
